import React, { useState, useEffect } from 'react';
import styles from './ActivitiesCreate.module.scss';
import { PanelRow } from '../../../components/panel/PanelRow';
import { Panel } from '../../../components/panel/Panel';
import { Dropdown, DropdownOptions } from '../../../components/dropdown/Dropdown';
import { Button } from '../../../components/button/Button';
import { useDispatch } from 'react-redux';
import {
    setName,
    setType,
    setDescription,
    setOrder,
    setPublished,
    setSalesforceObject,
    useAppStateActivitiesCreateSelector,
    setLaserficheProcess,
    setCalendlyLink,
    setExternalLink,
    setContentConfiguration,
    setPaymentRequired,
    setGuestEnabled,
    setPopularService,
    setPopularServiceOrder
} from './activitiesCreateSlice'
import {
    create,
    getActivitySObjectFields,
    getLaserficheForms
} from './activitiesCreateActionCreators';
import { Text } from '../../../components/text/Text';
import { Textarea } from '../../../components/textArea/Textarea';
import { Toggle } from '../../../components/toggle/Toggle';
import { Editor } from '@tinymce/tinymce-react';
import { ErrorValidationLabel, Reasoning } from '../../../components/errorValidationLabel/ErrorValidationLabel';
import { useLocation, useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BackArrow } from '../../../components/backArrow/BackArrow';
import { Spinner } from '../../../components/spinner/Spinner';
import { PageContainer } from '../../../components/pageContainer/PageContainer';
import { urlRegex, activityTypes } from '../../../constants';
import { Message } from 'semantic-ui-react';
import { useFeatureFlag } from '../../featureFlags/FeatureFlag';

export function ActivitiesCreate() {
    const {
        name,
        type,
        description,
        order,
        published,
        salesforceObjectId,
        laserficheProcessId,
        calendlyLink,
        externalLink,
        content,
        laserficheForms,
        loading,
        errorMessage,
        paymentRequired,
        guestEnabled,
        popularService,
        popularServiceOrder,
    } = useAppStateActivitiesCreateSelector(state => state);

    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation();
    const popularServicesFF = useFeatureFlag('EnablePopularServices');

    let [invalidName, setInvalidName] = useState<Reasoning | null>(null);
    let [invalidType, setInvalidType] = useState<Reasoning | null>(null);
    let [invalidDescription, setInvalidDescription] = useState<Reasoning | null>(null);
    let [invalidOrder, setInvalidOrder] = useState<Reasoning | null>(null);
    let [invalidSalesforceObject, setInvalidSalesforceObject] = useState<Reasoning | null>(null);
    let [invalidLaserficheProcess, setInvalidLaserficheProcess] = useState<Reasoning | null>(null);
    let [invalidCalendlyLink, setInvalidCalendlyLink] = useState<Reasoning | null>(null);
    let [invalidExternalLink, setInvalidExternalLink] = useState<Reasoning | null>(null);
    let [invalidContent, setInvalidContent] = useState<Reasoning | null>(null);
    const [containsError, setContainsError] = useState<boolean>(false);
    const [localFile, setLocalFile] = useState<any>();

    const action = (new URLSearchParams(location.search)).get('action');

    let activityTypesDropdownOptions: DropdownOptions[] = [
        {
            key: "1",
            text: "Laserfiche Form",
            value: activityTypes.LASERFICHE_FORM,
        },
        {
            key: "2",
            text: "External Link",
            value: activityTypes.EXTERNAL_LINK,
        },
        {
            key: "3",
            text: "Content",
            value: activityTypes.CONTENT,
        },
        {
            key: "4",
            text: "Custom Form",
            value: activityTypes.CUSTOM_FORM,
        }
    ];

    let salesforceObjects: DropdownOptions[] = [
        {
            key: "Test",
            text: "Test",
            value: "Test"
        },
        {
            key: "Case",
            text: "Case",
            value: "Case"
        },
        {
            key: "COE_Permit__c",
            text: "COE Permit",
            value: "COE_Permit__c"
        },
        {
            key: "Zoning_Inquiry__c",
            text: "Zoning Inquiry",
            value: "Zoning_Inquiry__c"
        }
    ];

    let laserficheProcesses: DropdownOptions[] = laserficheForms
        ? laserficheForms.businessProcesses.map((businessProcess, i) => {
            return {
                key: businessProcess.id.toString(),
                text: businessProcess.name,
                value: businessProcess.id.toString()
            }
        })
        : [];

    useEffect(() => {
        dispatch(getLaserficheForms());
    }, [dispatch]);

    let handleOnSubmit = async () => {
        let valid = validateForm();
        if (valid) {
            dispatch(create(history, action!));
        }
    }

    let handleSObjectChange = (value: string) => {
        dispatch(getActivitySObjectFields(value))
        dispatch(setSalesforceObject({ value }))
    }

    let clearValidators = () => {
        setInvalidName(null);
        setInvalidType(null);
        setInvalidDescription(null);
        setInvalidOrder(null);
        setInvalidSalesforceObject(null);
        setInvalidLaserficheProcess(null);
        setInvalidCalendlyLink(null);
        setInvalidExternalLink(null);
        setInvalidContent(null);
        setContainsError(false);
    }

    let validateForm = () => {
        clearValidators();
        let validForm = true;

        if (name.length === 0) {
            setInvalidName('Required');
            validForm = false;
        }
        if (type.length === 0) {
            setInvalidType('Required');
            validForm = false;
        }
        if (!description || description.length === 0) {
            if (popularServicesFF?.enabled) {
                dispatch(setDescription({ value: 'Press "start" to continue.' }));
            }
            else {
                setInvalidDescription('Required');
                validForm = false;
            }
        }
        if (order.toString().length === 0) {
            setInvalidOrder('Required');
            validForm = false;
        }
        if (calendlyLink.length && !urlRegex.test(calendlyLink)) {
            setInvalidCalendlyLink('Invalid');
            validForm = false;
        }
        if (type === activityTypes.LASERFICHE_FORM) {
            if (salesforceObjectId.length === 0) {
                setInvalidSalesforceObject('Required');
                validForm = false;
            }
            if (laserficheProcessId.length === 0) {
                setInvalidLaserficheProcess('Required');
                validForm = false;
            }
        }
        if (type === activityTypes.EXTERNAL_LINK) {
            if (externalLink.length === 0) {
                setInvalidExternalLink('Required');
                validForm = false;
            } else if (!urlRegex.test(externalLink)) {
                setInvalidExternalLink('Invalid');
                validForm = false;
            }
        } else if (type === activityTypes.CONTENT && !content.length) {
            setInvalidContent('Required');
            validForm = false;
        }
        if (!validForm) {
            setContainsError(true);
        }

        return validForm;
    }

    let imgUpload = (e: any) => {
        setLocalFile(URL.createObjectURL(e.target.files[0]));
    }

    if (errorMessage) {
        return <Message error header={"Error"} content={errorMessage} />;
    }

    return (
        <PageContainer>
            <div className={styles.pageHeader}>
                <div className='headerContainer'>
                    <h2>{t('Activity.NewActivity')}</h2>
                    <Spinner isLoading={loading}/>
                </div>
                <div className={styles.submitContainer}>
                    <Button color='blue' onClick={handleOnSubmit}>{t('Util.Submit')}</Button>
                </div>
            </div>
            {containsError && <p className={styles.topError}>{t('Util.FormContainsErrors')}</p>}
            <BackArrow url="/activities" text={t('Activity.BackToActivities')} />
            <PanelRow>
                <Panel>
                    <div className={styles.innerPanelContainer}>
                        <h3>{t('Activity.GeneralInformation')}</h3>
                        <div className={styles.row}>
                            <div className={styles.inputLabel}>{t('Activity.ActivityName')}</div>
                            <Text
                                value={name}
                                onChange={(e: any) => dispatch(setName({ value: e.target.value }))}
                                aria-label="Activity name"
                            />
                            {invalidName ? <ErrorValidationLabel fieldDescription={'Name'} id="activity-name-error" /> : null}
                        </div>
                        <div className={styles.row}>
                            <div className={styles.inputLabel}>{t('Activity.ActivityType')}</div>
                            <Dropdown value={type} options={activityTypesDropdownOptions} placeholder={activityTypesDropdownOptions[0].text} onChange={(e: any, data: any) => dispatch(setType({ value: data.value }))} aria-label="Activity type" />
                            {invalidType ? <ErrorValidationLabel fieldDescription={'Type'} id="activity-type-error" /> : null}
                        </div>
                        <div className={styles.row} style={popularServicesFF?.enabled ? {display: 'none'} : {}}>
                            <div className={styles.inputLabel}>{t('Activity.ActivityDescription')}</div>
                            <Textarea value={description} onChange={(e: any, data: any) => dispatch(setDescription({ value: data.value }))} aria-label="Activity description" />
                            {invalidDescription ? <ErrorValidationLabel fieldDescription={'Description'} id="activity-desc-error" /> : null}
                        </div>
                        <div className={styles.row}>
                            <div className={styles.orderContainer}>
                                <Toggle value={guestEnabled} onChange={(e: any, data: any) => dispatch(setGuestEnabled({ value: data.checked }))} text={t('Activity.GuestEnabled')} />
                            </div>
                        </div>
                        <div className={styles.row}>
                            <div className={styles.orderContainer}>
                                <Toggle value={published} onChange={(e: any, data: any) => dispatch(setPublished({ value: data.checked }))} text={t('Activity.Published')} />
                            </div>
                        </div>
                        <div className={styles.row} style={popularServicesFF?.enabled ? {display: 'none'} : {}}>
                            <div className={styles.inputLabel}>{t('Activity.Order')}</div>
                            <div className={styles.orderContainer}>
                                <Text value={order} onChange={(e: any, data: any) => dispatch(setOrder({ value: data.value }))} type='number' min={0} aria-label="Display order" />
                            </div>
                            {invalidOrder ? <ErrorValidationLabel fieldDescription={'Order'} id="activity-order-error" /> : null}
                        </div>
                        {popularServicesFF?.enabled &&
                            <div className={styles.row}>
                                <div className={styles.inputLabel} style={{ marginBottom: '10px' }}>{t('Activity.PopularService')}</div>                            
                                <Toggle value={popularService} onChange={(e: any, data: any) => dispatch(setPopularService(data.checked))} text={t('Activity.IsPopularService')} />
                                <br/>
                                <div style={!popularService ? {display: 'none'} : {}}>
                                        <div className={styles.inputLabel}>{t('Popular Service Icon')}</div>
                                        <div className={styles.inputLabelNote}>{'Please note that the recommended image size for icons is 32 by 32 pixels.'}</div>
                                    </div>
                                <div className={styles.buttonWrap} style={!popularService ? {display: 'none'} : {}}>
                                    {localFile && popularService &&
                                        <img src={localFile} alt="Popular service icon" className={styles.activityIcon} />
                                    }
                                    <>
                                        <label className={styles.fileUploadButton} htmlFor="activityIconFile">{localFile ? 'Upload New Icon for Service' : t('Activity.UploadIconForService')}</label>
                                        <input type="file" id="activityIconFile" name="upload" onChange={(e) => imgUpload(e)} />
                                    </>
                                </div>
                                <div style={!popularService ? {display: 'none'} : {}}>
                                    <br />
                                    <div className={styles.inputLabel} style={{ marginBottom: '10px' }}>{t('Popular Service Order')}</div>
                                    <div className={styles.orderContainer}>
                                        <Text value={popularServiceOrder ? popularServiceOrder : 0} onChange={(e: any, data: any) => dispatch(setPopularServiceOrder({ value: data.value }))} type='number' min={0} aria-label="Popular service order" />
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                </Panel>
            </PanelRow>
            {(type === activityTypes.LASERFICHE_FORM || type === activityTypes.CUSTOM_FORM) &&
                <PanelRow>
                    <Panel>
                        <div className={styles.innerPanelContainer}>
                            <h3>{t('Activity.CalendlyLink')}</h3>
                            <div className={styles.row}>
                                <div className={styles.inputLabel}>{t('Activity.URL')}</div>
                                <Text value={calendlyLink} onChange={(e: any, data: any) => dispatch(setCalendlyLink({ value: data.value }))} aria-label="Calendly link URL" />
                                {invalidCalendlyLink ? <ErrorValidationLabel fieldDescription={'URL'} reasoning={invalidCalendlyLink} id="activity-calendly-error" /> : null}
                            </div>
                        </div>
                    </Panel>
                </PanelRow>
            }
            {type === activityTypes.LASERFICHE_FORM &&
                <>
                    <PanelRow>
                        <Panel>
                            <div className={styles.innerPanelContainer}>
                                <h3>{t('Activity.SalesforceConfiguration')}</h3>
                                <div className={styles.row}>
                                    <div className={styles.inputLabel}>{t('Activity.SalesforceObject')}</div>
                                    <Dropdown value={salesforceObjectId} options={salesforceObjects} placeholder={`${t('Activity.SalesforceObject')}...`} onChange={(e: any, data: any) => handleSObjectChange(data.value)} aria-label="Salesforce object" />
                                    {invalidSalesforceObject ? <ErrorValidationLabel fieldDescription={t('Activity.SalesforceObject')} id="activity-sf-object-error" /> : null}
                                </div>
                            </div>
                        </Panel>
                    </PanelRow>
                    <PanelRow>
                        <Panel>
                            <div className={styles.innerPanelContainer}>
                                <h3>{t('Activity.LaserficheFormConfiguration')}</h3>
                                <div className={styles.row}>
                                    <div className={styles.inputLabel}>{t('Activity.LaserficheProcess')}</div>
                                    <Dropdown value={laserficheProcessId} options={laserficheProcesses} placeholder={'Laserfiche process...'} onChange={(e: any, data: any) => dispatch(setLaserficheProcess({ value: data.value }))} aria-label="Laserfiche process" />
                                    {invalidLaserficheProcess ? <ErrorValidationLabel fieldDescription={'Laserfiche Process'} id="activity-lf-process-error" /> : null}
                                </div>
                            </div>
                        </Panel>
                    </PanelRow>
                </>
            }
            {type === activityTypes.EXTERNAL_LINK &&
                <PanelRow>
                    <Panel>
                        <div className={styles.innerPanelContainer}>
                            <h3>{t('Activity.ExternalLinkConfiguration')}</h3>
                            <div className={styles.row}>
                                <div className={styles.inputLabel}>{t('Activity.URL')}</div>
                                <Text value={externalLink} onChange={(e: any, data: any) => dispatch(setExternalLink({ value: data.value }))} aria-label="External link URL" />
                                {invalidExternalLink ? <ErrorValidationLabel fieldDescription={'URL'} reasoning={invalidExternalLink} id="activity-external-link-error" /> : null}
                            </div>
                        </div>
                    </Panel>
                </PanelRow>
            }
            {type === activityTypes.CONTENT &&
                <PanelRow>
                    <Panel>
                        <div className={styles.innerPanelContainer}>
                            <h3>{t('Activity.ContentConfiguration')}</h3>
                            <div className={styles.row}>
                                <div className={styles.inputLabel}>{t('Activity.Content')}</div>
                                <Editor value={content} onEditorChange={(content, editor) => dispatch(setContentConfiguration({ value: content }))}/>
                                {invalidContent ? <ErrorValidationLabel fieldDescription={'Content'} id="activity-content-error" /> : null}
                            </div>
                        </div>
                    </Panel>
                </PanelRow>
            }
            <PanelRow>
                <Panel>
                    <div className={styles.innerPanelContainer}>
                        <h3>{t('Activity.PaymentConfiguration')}</h3>
                        <div className={styles.row}>
                            <Toggle value={paymentRequired} onChange={(e: any, data: any) => dispatch(setPaymentRequired(data.checked))} text={t('Activity.PaymentRequired')} />
                        </div>
                    </div>
                </Panel>
            </PanelRow>
        </PageContainer>
    );
}
